import { Member } from '@services/member/member/member.types';
import { formatDate } from '@utils/dates';
import { formatMoney } from '@utils/numbers';
import { CONTRIBUTION_TYPE, FAMILY_STATUS, GENDER, PAYMENT_TYPE } from '@utils/enumTranslation';
import moment from 'moment';

/**
 * Génère la section des informations administratives
 */
export const generateAdministrativeSection = (member: Member): string => {
    return `
        <div class="section">
            <div class="section-header">${member.fullname}</div>
            <div class="section-content">
                <div class="grid-4">
                    <div class="info-row compact">
                        <span class="label">N°COSEM:</span> 
                        <span class="value">${member.cosemNumber}</span>
                    </div>
                    <div class="info-row compact">
                        <span class="label">Matricule:</span> 
                        <span class="value">${member.administrativeInformation.registrationNumber || ''}</span>
                    </div>
                    <div class="info-row compact">
                        <span class="label">Ancien matricule:</span> 
                        <span class="value">${member.administrativeInformation.oldRegistrationNumber || ''}</span>
                    </div>
                    <div class="info-row compact">
                        <span class="label">Organisme:</span> 
                        <span class="value">${member.administrativeInformation.organizationLabel || ''}</span>
                    </div>
                    <div class="info-row compact">
                        <span class="label">Date d'adhésion:</span> 
                        <span class="value">${formatDate(member.administrativeInformation.registeredAt)}</span>
                    </div>
                    <div class="info-row compact">
                        <span class="label">Code État:</span> 
                        <span class="value">${member.administrativeInformation.stateCodeLabel || ''}</span>
                    </div>
                    <div class="info-row compact">
                        <span class="label">Service:</span> 
                        <span class="value">${member.administrativeInformation.serviceCodeLabel || ''}</span>
                    </div>
                    <div class="info-row compact">
                        <span class="label">Type de cotisation:</span> 
                        <span class="value">${CONTRIBUTION_TYPE[member.administrativeInformation.contributionType] || ''}</span>
                    </div>
                </div>
            </div>
        </div>
    `;
};

/**
 * Génère la section des informations importantes
 */
export const generatePersonalInfoSection = (member: Member): string => {
    const deceasedChildrenCount = member.children.filter((child) => child.deceased).length;
    
    return `
        <div class="section">
            <div class="section-header">Informations importantes</div>
            <div class="section-content">
                <div class="grid">
                    <div class="info-row compact">
                        <span class="label">Nom:</span> 
                        <span class="value">${member.personalInformation.lastname.toUpperCase()}</span>
                    </div>
                    <div class="info-row compact">
                        <span class="label">Prénom:</span> 
                        <span class="value">${member.personalInformation.firstname.toUpperCase()}</span>
                    </div>
                    <div class="info-row compact">
                        <span class="label">Nom de naissance:</span> 
                        <span class="value">${member.personalInformation.birthName?.toUpperCase() || ''}</span>
                    </div>
                    <div class="info-row compact">
                        <span class="label">Genre:</span> 
                        <span class="value">${GENDER[member.personalInformation.gender] || ''}</span>
                    </div>
                    <div class="info-row compact">
                        <span class="label">Date de naissance:</span> 
                        <span class="value">${formatDate(member.personalInformation.birthDate)}</span>
                    </div>
                    <div class="info-row compact">
                        <span class="label">Situation familiale:</span> 
                        <span class="value">${FAMILY_STATUS[member.personalInformation.familyStatus] || ''}</span>
                    </div>
                    <div class="info-row compact">
                        <span class="label">N° Téléphone:</span> 
                        <span class="value">${member.contactInformation.phone || ''}</span>
                    </div>
                    <div class="info-row compact">
                        <span class="label">Mail:</span> 
                        <span class="value">${member.contactInformation.email || ''}</span>
                    </div>
                    <div class="info-row compact">
                        <span class="label">Adresse:</span> 
                        <span class="value">${member.contactInformation.address || ''}</span>
                    </div>
                    ${member.contactInformation.additionalAddress ? `
                    <div class="info-row compact">
                        <span class="label">Complément d'adresse:</span> 
                        <span class="value">${member.contactInformation.additionalAddress}</span>
                    </div>
                    ` : ''}
                    <div class="info-row compact">
                        <span class="label">Code postal:</span> 
                        <span class="value">${member.contactInformation.zipCode || ''}</span>
                    </div>
                    <div class="info-row compact">
                        <span class="label">Ville:</span> 
                        <span class="value">${member.contactInformation.city || ''}</span>
                    </div>
                    <div class="info-row compact">
                        <span class="label">Nombre d'enfants:</span> 
                        <span class="value">${member.children.length}</span>
                    </div>
                    <div class="info-row compact">
                        <span class="label">Enfant(s) à charge:</span> 
                        <span class="value">${member.children.filter((child) => child.dependent).length}</span>
                    </div>
                    ${deceasedChildrenCount > 0 ? `
                    <div class="info-row compact">
                        <span class="label">Enfant(s) décédé(s):</span> 
                        <span class="value">${deceasedChildrenCount}</span>
                    </div>
                    ` : ''}
                </div>
            </div>
        </div>
    `;
};

/**
 * Génère la section des prestations sociales (si présentes)
 */
export const generateSocialBenefitsSection = (member: Member): string => {
    if (member.lastSocialBenefits.length === 0) return '';
    
    return `
        <div class="section">
            <div class="section-header">Dernières prestations sociales</div>
            <div class="section-content">
                <table>
                    <thead>
                        <tr>
                            <th>Attribué le</th>
                            <th>Bénéficiaire</th>
                            <th>Montant</th>
                            <th>Intitulé</th>
                            <th>Commentaire</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${member.lastSocialBenefits.map(benefit => `
                            <tr>
                                <td>${moment(benefit.assignedAt).format('DD/MM/YYYY')}</td>
                                <td>${benefit.recipientFullname}</td>
                                <td>${formatMoney(benefit.amount)}</td>
                                <td>${benefit.label}</td>
                                <td>${benefit.observation || ''}</td>
                            </tr>
                        `).join('')}
                    </tbody>
                </table>
            </div>
        </div>
    `;
};

/**
 * Génère la section des prestations financières (si présentes)
 */
export const generateFinancialBenefitsSection = (member: Member): string => {
    if (!member.lastFinancialBenefit) return '';
    
    const { lastFinancialBenefit } = member;
    
    return `
        <div class="section">
            <div class="section-header">Prestations financières en cours</div>
            <div class="section-content">
                <div class="grid">
                    <div class="info-row compact">
                        <span class="label">Code:</span> 
                        <span class="value">${lastFinancialBenefit.code}</span>
                    </div>
                    <div class="info-row compact">
                        <span class="label">Demandé le:</span> 
                        <span class="value">${formatDate(lastFinancialBenefit.requestedAt)}</span>
                    </div>
                    ${lastFinancialBenefit.financialBenefitSchedules[0] ? `
                    <div class="info-row compact">
                        <span class="label">Première échéance:</span> 
                        <span class="value">${formatDate(lastFinancialBenefit.financialBenefitSchedules[0].scheduledAt)}</span>
                    </div>
                    ` : ''}
                    <div class="info-row compact">
                        <span class="label">Montant du prêt:</span> 
                        <span class="value">${formatMoney(lastFinancialBenefit.amount)}</span>
                    </div>
                    <div class="info-row compact">
                        <span class="label">Total:</span> 
                        <span class="value">${formatMoney(lastFinancialBenefit.total)}</span>
                    </div>
                    <div class="info-row compact">
                        <span class="label">Nb d'échéances:</span> 
                        <span class="value">${lastFinancialBenefit.financialBenefitSchedules.length}</span>
                    </div>
                    ${lastFinancialBenefit.financialBenefitSchedules[0] ? `
                    <div class="info-row compact">
                        <span class="label">Montant mensuel:</span> 
                        <span class="value">${formatMoney(lastFinancialBenefit.financialBenefitSchedules[0].amount)}</span>
                    </div>
                    ` : ''}
                </div>
                
                ${lastFinancialBenefit.nextFinancialBenefitSchedules.length > 0 ? `
                <div class="separator"></div>
                <table>
                    <thead>
                        <tr>
                            <th>Année</th>
                            <th>Mois</th>
                            <th>Montant</th>
                            <th>Pointage</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${lastFinancialBenefit.nextFinancialBenefitSchedules.map(schedule => `
                            <tr>
                                <td>${moment(schedule.scheduledAt).format('YYYY')}</td>
                                <td>${moment(schedule.scheduledAt).format('MMMM')}</td>
                                <td>${formatMoney(schedule.total)}</td>
                                <td>${schedule.isPointed ? 'Oui' : 'Non'}</td>
                            </tr>
                        `).join('')}
                    </tbody>
                </table>
                ` : ''}
            </div>
        </div>
    `;
};

/**
 * Génère la section des cotisations (si présentes)
 */
export const generateContributionsSection = (member: Member): string => {
    if (member.lastContributions.length === 0) return '';
    
    return `
        <div class="section">
            <div class="section-header">Dernières cotisations</div>
            <div class="section-content">
                <table>
                    <thead>
                        <tr>
                            <th>Année</th>
                            <th>Date de paiement</th>
                            <th>Montant</th>
                            <th>Nature</th>
                            <th>Commentaire</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${member.lastContributions.map(contribution => `
                            <tr>
                                <td style="text-align: center;">${contribution.year}</td>
                                <td>${contribution.dateContributed ? moment(contribution.dateContributed).format('DD/MM/YYYY') : ''}</td>
                                <td>${formatMoney(contribution.amount)}</td>
                                <td>${PAYMENT_TYPE[contribution.paymentType] || ''}</td>
                                <td>${contribution.observation || ''}</td>
                            </tr>
                        `).join('')}
                    </tbody>
                </table>
            </div>
        </div>
    `;
};

/**
 * Génère la section des réservations (si présentes)
 */
export const generateBookingsSection = (member: Member): string => {
    if (member.lastBookings.length === 0) return '';
    
    return `
        <div class="section">
            <div class="section-header">Dernières réservations</div>
            <div class="section-content">
                <table>
                    <thead>
                        <tr>
                            <th>Logement</th>
                            <th>Date d'arrivée</th>
                            <th>Date de départ</th>
                            <th>Nombre de personnes</th>
                            <th>Prix</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${member.lastBookings.map(booking => `
                            <tr>
                                <td>${booking.apartment.fullname}</td>
                                <td>${formatDate(booking.arrivalDate)}</td>
                                <td>${formatDate(booking.departureDate)}</td>
                                <td>${booking.peopleCount}</td>
                                <td>${formatMoney(booking.rentalPrice)}</td>
                            </tr>
                        `).join('')}
                    </tbody>
                </table>
            </div>
        </div>
    `;
};
